import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { formatIcu } from './numberArgs';
import { TEACHING_FALLBACKS_EN } from './teachingFallbacks';

// i18n bootstrap — the React equivalent of Flutter's gen-l10n
// AppLocalizations. The message bundles are generated from the same ARB
// files (scripts/convert-arb.mjs → src/i18n/locales/<code>.json).
//
// English (the fallback language) is bundled into the entry chunk. Every
// other locale is its own lazily-loaded chunk: a user only ever needs one of
// them, and bundling all seven put ~470 kB of unused strings on the critical
// path of every first load. main.tsx awaits the active locale before the App
// renders (no flash of English), and changeUiLanguage() loads a bundle before
// switching to it. The service worker precaches every chunk, so offline
// switching still works once installed.
//
// ICU plugin renders the `{count, plural, …}` / `{name}` placeholders that
// the ARB strings use, matching Flutter's intl message format 1:1.

type Bundle = Record<string, string>;

const eagerModules = import.meta.glob('./locales/en.json', { eager: true }) as Record<
  string,
  { default: Bundle }
>;
const lazyModules = import.meta.glob(['./locales/*.json', '!./locales/en.json']) as Record<
  string,
  () => Promise<{ default: Bundle }>
>;

const codeOf = (path: string) => path.replace(/^\.\/locales\//, '').replace(/\.json$/, '');

const resources: Record<string, { translation: Bundle }> = {};
for (const [path, mod] of Object.entries(eagerModules)) {
  resources[codeOf(path)] = { translation: mod.default };
}
// Teaching strings not in the ARB yet: UNDER the generated English, so an
// ARB key always wins (see teachingFallbacks.ts).
if (resources.en) resources.en = { translation: { ...TEACHING_FALLBACKS_EN, ...resources.en.translation } };
const loaders: Record<string, () => Promise<{ default: Bundle }>> = {};
for (const [path, load] of Object.entries(lazyModules)) {
  loaders[codeOf(path)] = load;
}

/** Locale codes the UI is translated into — derived from the bundled files. */
export const SUPPORTED_LOCALES = [...new Set([...Object.keys(resources), ...Object.keys(loaders)])].sort();

/** Makes the bundle for [code] available to i18next. Resolves immediately when
 *  it is already loaded (English always is) or unknown; a failed chunk fetch
 *  resolves too — i18next then falls back to English, as for a missing key. */
export async function loadLocaleBundle(code: string): Promise<void> {
  if (resources[code]) return;
  const load = loaders[code];
  if (!load) return;
  try {
    const mod = await load();
    resources[code] = { translation: mod.default };
    // Before init this is a no-op for i18next (init reads `resources`); after
    // init it registers the bundle with the running instance.
    if (i18n.isInitialized) i18n.addResourceBundle(code, 'translation', mod.default, true, true);
  } catch {
    // Offline before the chunk was ever cached: English fallback.
  }
}

/** Loads [code]'s bundle, then switches i18next to it. Use instead of
 *  i18n.changeLanguage so a lazily-loaded locale never renders as English. */
export async function changeUiLanguage(code: string): Promise<void> {
  await loadLocaleBundle(code);
  await i18n.changeLanguage(code);
}

/**
 * Resolution chain when the user hasn't explicitly chosen a UI locale,
 * mirroring main.dart's localeListResolutionCallback:
 *   1. stored preference (settings sync writes this) → use it
 *   2. browser languages, first that matches a bundled locale → use it
 *   3. English fallback
 */
/** The browser/OS language mapped to a bundled locale (else English). This is
 *  what "System (automatic)" resolves to — used at boot AND whenever the user
 *  clears their explicit locale, so "system" actually follows the device. */
export function systemLocale(): string {
  for (const nav of navigator.languages ?? [navigator.language]) {
    const base = nav.split('-')[0]?.toLowerCase();
    if (base && SUPPORTED_LOCALES.includes(base)) return base;
  }
  return SUPPORTED_LOCALES.includes('en') ? 'en' : (SUPPORTED_LOCALES[0] ?? 'en');
}

function detectInitialLocale(stored?: string | null): string {
  if (stored && SUPPORTED_LOCALES.includes(stored)) return stored;
  return systemLocale();
}

// ICU message formatting. We deliberately DON'T use the `i18next-icu` package:
// at v2.4.3 it does `import IntlMessageFormat from 'intl-messageformat'` (a
// default import), but intl-messageformat 10.7+ only exposes the class as a
// NAMED export — so that resolves to a non-constructor ("IntlMessageFormat is
// not a constructor"), every parse threw, and its default error handler
// silently returned the RAW source, so `{name}` / `{count, plural, …}` rendered
// literally everywhere. This tiny i18nFormat uses the named export directly —
// identical ICU rendering (placeholders, plural/select), under our control.
// Plain strings (no `{`) skip straight through; a malformed message or a
// missing variable returns the source unchanged instead of throwing.
const icuFormat = {
  type: 'i18nFormat' as const,
  parse(res: string, options: Record<string, unknown>, lng: string, _ns?: string, key?: string): string {
    if (typeof res !== 'string' || !res.includes('{')) return res;
    try {
      // Numeric arguments render locale-grouped ("10,000" / "10.000"), as
      // Flutter's decimalPattern placeholders do — see numberArgs.ts.
      return formatIcu(res, options, lng, key);
    } catch (err) {
      // Most commonly: a placeholder the message needs wasn't passed at the call
      // site (IntlMessageFormat throws). Surface it in dev — and keep the raw
      // `{token}` visible there — so the missing value gets fixed.
      if (import.meta.env.DEV) {
        console.warn('[i18n] ICU format failed for:', res, err);
        return res;
      }
      // In prod, degrade gracefully: fill the simple `{name}` placeholders we do
      // have values for and drop the rest, so a user NEVER sees a literal
      // "{token}" (the "{queries} queries · {entries} …" bug). Complex ICU like
      // `{n, plural, …}` that failed to parse is left as-is — a real dev bug.
      return res.replace(/\{(\w+)\}/g, (_match, key: string) =>
        options[key] == null ? '' : String(options[key]),
      );
    }
  },
};

/** Initialises i18next with the stored UI locale (null → browser/system).
 *  Synchronous, as before; for a locale other than English the strings then
 *  arrive with [activeLocaleReady] — main.tsx awaits it before rendering. */
export function initI18n(storedLocale?: string | null): typeof i18n {
  const lng = detectInitialLocale(storedLocale);
  void i18n
    .use(icuFormat)
    .use(initReactI18next)
    .init({
      resources,
      lng,
      fallbackLng: 'en',
      interpolation: { escapeValue: false }, // React already escapes
      returnNull: false,
    });
  readyPromise = loadLocaleBundle(lng);
  return i18n;
}

let readyPromise: Promise<void> = Promise.resolve();

/** Resolves once the bundle of the locale chosen by [initI18n] is loaded
 *  (immediately for English, or when the chunk could not be fetched). */
export function activeLocaleReady(): Promise<void> {
  return readyPromise;
}

export default i18n;
